import './app.scss';
import domready from 'domready';
import 'lazysizes';
import { getOffset } from './utilities/js/helper';
import { loadLazyImages } from './utilities/js/lazy-image-loader';
import { EventScroller } from './utilities/js/event-scroller';
import { ResizeHandler } from './utilities/js/resize-handler';
import { MediaMatchHandler } from './utilities/js/media-match-handler';
import { Bookmark } from './components/bookmark/bookmark';

// GLOBAL OPTIONS
window.OPTIONS = { // eslint-disable-line
    loader: '<span class="loading"><span class="loader"></span></span>',
    breakpoints: {
        s: 320,
        m: 768,
        l: 1024,
        xl: 1280,
        xxl: 1440,
        xxxl: 1920
    },
    speed: 500
};

window.eventScroller = new EventScroller();
window.resizeHandler = new ResizeHandler();
window.mediaMatch = new MediaMatchHandler();

window.youtubeApi = false;
window.youtubeApiLoading = false;
window.anchorTimeout = null;
window.bookmark = new Bookmark();
window.accordionLoaded = false;
window.isWaitingForAnchor = false;

const browserCheck = () => {
    window.browser = {
        csshover: !window.matchMedia('(pointer:coarse)').matches,
        windows: (/windows/i).test(navigator.userAgent),
        ios: (/iPhone|iPad|iPod/i).test(navigator.userAgent),
        iphone: (/iPhone|iPod/i).test(navigator.userAgent),
        android: (/(android)/i).test(navigator.userAgent),
        firefox: (/firefox/i).test(navigator.userAgent),
        safari: (/Version\/[\d.]+.*Safari/).test(navigator.userAgent)
    };

    Object.entries(window.browser).forEach(([key, value]) => {
        document.documentElement.classList.add((!value) ? `no-${key}` : `${key}`);
    });
};

window.checkInView = (entries) => {
    entries.forEach((entry) => {
        if (entry.isIntersecting) {
            if (!entry.target.classList.contains('inview')) {
                entry.target.classList.add('inview');

                if (typeof entry.target.dataset.footer === 'undefined') {
                    window.inViewObserver.unobserve(entry.target);
                }
            }
        }
    });
};

window.inViewObserver = new IntersectionObserver(window.checkInView, {
    threshold: 0,
    rootMargin: '0px 100% -10% 100%'
});

const jumpToAnchor = (targetId, focusTarget = true) => {
    const $target = document.getElementById(targetId);

    if ($target !== null) {
        const anchorDimensions = getOffset($target);
        const navigationOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--main-height') || '0', 10);

        // An alert banner above the siteheader pushes the whole sticky area
        // down (alert-banner.js sets --siteheader-top on the body).
        const siteheaderTop = parseInt(getComputedStyle(document.body).getPropertyValue('--siteheader-top') || '0', 10) || 0;

        let anchorDimensionsTop = Math.floor(anchorDimensions.top) - navigationOffset - siteheaderTop;
        const anchorNavigation = document.querySelector('[data-anchor-navigation="root"]');
        const tabNavigation = document.querySelector('[data-tab-nav="root"]');

        // because the anchor navigation is now a fixed element, we have to reduce the scroll to position to prevent overlapping
        if (anchorNavigation) {
            anchorDimensionsTop = anchorDimensionsTop - Math.floor(anchorNavigation.offsetHeight + 28);
        }

        if (tabNavigation) {
            anchorDimensionsTop = anchorDimensionsTop - Math.floor(tabNavigation.offsetHeight);
        }

        // Additional sticky elements below the siteheader (e.g. a page-level
        // progress bar) opt in via data-jump-offset so they do not cover the
        // jump target.
        document.querySelectorAll('[data-jump-offset]').forEach(($offsetElement) => {
            anchorDimensionsTop = anchorDimensionsTop - Math.floor($offsetElement.offsetHeight);
        });

        window.scrollTo({
            top: anchorDimensionsTop,
            left: 0,
            behavior: 'smooth'
        });

        // Skippable for callers that only reposition the viewport (e.g. an
        // accordion keeping its toggle focused).
        if (focusTarget) {
            setTimeout(() => {
                if ($target) {
                    $target.setAttribute('tabindex', '0');

                    // The viewport is already positioned offset-aware above -
                    // the implicit focus scrolling would shift it again.
                    $target.focus({ preventScroll: true });

                    setTimeout(() => {
                        $target.removeAttribute('tabindex');
                    }, 400);
                }
            }, 1000);
        }
    }
};

// Components outside this bundle (e.g. the giftorder category accordion)
// reuse the offset-aware scrolling instead of duplicating the calculation.
window.jumpToAnchor = jumpToAnchor;

const initDataForCheckboxes = ($content) => {
    const $$checkboxTrigger = $content.querySelectorAll('[data-for-checkbox]');

    if ($$checkboxTrigger.length > 0) {
        const resetAllTriggersByReference = (reference, state) => {
            const $allReferenceCheckboxes = $content.querySelectorAll(`[data-for-checkbox="${reference}"]`);

            if ($allReferenceCheckboxes.length > 0) {
                $allReferenceCheckboxes.forEach($checkboxTrigger => {
                    let triggerActiveAttribute = 'aria-pressed';

                    if ($checkboxTrigger.dataset.activeAttribute !== undefined) {
                        triggerActiveAttribute = $checkboxTrigger.dataset.activeAttribute;
                    }

                    $checkboxTrigger.setAttribute(triggerActiveAttribute, state);
                });
            }
        };

        $$checkboxTrigger.forEach($checkboxTrigger => {
            const checkboxReference = $checkboxTrigger.dataset.forCheckbox;
            let triggerActiveAttribute = 'aria-pressed';

            if ($checkboxTrigger.dataset.activeAttribute !== undefined) {
                triggerActiveAttribute = $checkboxTrigger.dataset.activeAttribute;
            }

            const $checkbox = $content.querySelector(`#${checkboxReference}`);

            $checkboxTrigger.onclick = () => {
                if ($checkbox.checked === true) {
                    $checkbox.checked = false;
                    resetAllTriggersByReference(checkboxReference, false);
                } else {
                    $checkbox.checked = true;
                    $checkboxTrigger.setAttribute(triggerActiveAttribute, $checkbox.checked);
                }
            };

            if ($checkbox) {
                $checkbox.onchange = () => {
                    $checkboxTrigger.setAttribute(triggerActiveAttribute, $checkbox.checked);
                };
            }
        });
    }
};

const initAnchors = ($content) => {
    const $$anchor = $content.querySelectorAll('a[href*="#"]:not([data-anchor-navigation])');

    for (let i = 0; i < $$anchor.length; i++) {
        const $anchor = $$anchor[i];

        // Runs again for every content.loaded, so anchors that already have
        // their handler must not be bound twice.
        if ($anchor.dataset.anchorInitialized === 'true') {
            continue;
        }

        $anchor.dataset.anchorInitialized = 'true';

        $anchor.addEventListener('click', (e) => {
            let baseUrl = $anchor.getAttribute('href').split('#')[0];
            const targetId = $anchor.getAttribute('href').split('#')[1];
            const currentUrl = window.location.href;

            if (baseUrl === '') {
                baseUrl = currentUrl;
            }

            if (baseUrl === currentUrl) {
                jumpToAnchor(targetId);
                e.preventDefault();
            }
        });
    }
};

const initContent = ($content) => {
    const locationHash = window.location.hash;

    initAnchors($content);

    window.addEventListener('content.loaded', (e) => {
        initDataForCheckboxes($content);

        // Anchors inside AJAX-rendered markup (e.g. the giftorder cart) get
        // the same offset-aware jump handling as the initial content.
        initAnchors(e.detail.$context || $content);

        const $$checkboxButtons = $content.querySelectorAll('input[type="checkbox"][data-checkbox-button]');
        $$checkboxButtons.forEach($toggle => {
            $toggle.addEventListener('keydown', (event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    $toggle.checked = !$toggle.checked;
                }
            });
        });

        if (e.detail.ajax && !window.isWaitingForAnchor) {
            const hash = window.location.hash;
            if (hash) {
                const targetId = hash.split('#')[1];
                let wait = 0;
                window.isWaitingForAnchor = true;

                const waitForAnchor = setInterval(() => {
                    if (document.getElementById(targetId)) {
                        clearInterval(waitForAnchor);

                        setTimeout(() => {
                            jumpToAnchor(targetId);
                        }, 400);
                    } else {
                        wait++;

                        if (wait >= 40) {
                            window.isWaitingForAnchor = false;
                            clearInterval(waitForAnchor);
                        }
                    }
                }, 300);
            }
        }
    });

    if (locationHash !== undefined) {
        setTimeout(() => {
            jumpToAnchor(locationHash.split('#')[1]);
        }, 400);
    }

    window.bookmark.initialize(document);

    loadLazyImages();
};

domready(function () {
    const initApplication = () => {
        browserCheck();

        initContent(document);

        window.eventScroller.init();
        window.resizeHandler.init();

        const contentUpdateEvent = new CustomEvent('content.loaded', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: document
            }
        });

        window.dispatchEvent(contentUpdateEvent);

        window.dataInview = document.querySelectorAll('[data-inview]:not(.inview)');
        setTimeout(() => {
            window.dataInview.forEach((dataInView) => {
                window.inViewObserver.observe(dataInView);
            });
        }, 500);
    };

    initApplication();
});
